import { action, computed, observable } from 'mobx';

import { IApiClient } from '../data/client';
import { GenerateConfigurationPdfCommand, ShoppingContext, StoreContext } from '../data/model';
import { IShareConfiguratorUrlProvider } from '../shared/BaseShareConfiguratorUrlProvider';
import { AsyncCommand } from '../shared/common';
import { IImagePreloader } from '../shared/ImagePreloader';
import { INavigationService } from '../shared/NavigationService';
import { IPdfUrlProvider } from '../shared/PdfUrlProvider';
import { ZipBuilder } from '../shared/ZipBuilder';
import { Ean13CodeShareState } from './components/ConfigurationShare/Ean13CodeShare/Ean13CodeShareState';
import IConfigurationPageQuery from './IConfigurationPageQuery';
import { FinalVisualizationBuilder } from './VisualizationBuilder';

export interface ConfigurationShareContext {
  productNumber: string;
  visualizationMode: string;
  visualizationShots: string[];
  query: IConfigurationPageQuery;
}

export type DownloadImageMode = 'single' | 'zip';

export class ConfigurationShareState {
  @observable public code: string = '';

  @observable public downloadImageMode: DownloadImageMode = 'zip';
  @observable public downloadImageUrl: string | undefined = undefined;

  public downloadAsPdfCommand: AsyncCommand;
  public downloadZipCommand: AsyncCommand;
  public downloadImageCommand: AsyncCommand;

  private zipBuilder: ZipBuilder;

  private shoppingContext: ShoppingContext;

  public ean13CodeState: Ean13CodeShareState;

  @computed
  get pdfName() {
    return `Flokk-chair-${this.code}.pdf`;
  }

  @computed
  get zipName() {
    return `Flokk-chair-${this.code}.zip`;
  }

  @computed
  get downloadImageName() {
    return `Flokk-chair-${this.code}.png`;
  }

  @action
  public setCode(code: string) {
    this.code = code;
    this.ean13CodeState.setConfigurationCode(code);
  }

  @action
  public setDownloadImageMode(mode: DownloadImageMode) {
    this.downloadImageMode = mode;
  }

  @action
  public setDownloadImageUrl(url: string) {
    this.downloadImageUrl = url;
  }

  constructor(
    private readonly client: IApiClient,
    private readonly navigation: INavigationService,
    private readonly imagePreloader: IImagePreloader,
    private readonly pdfUrlProvider: IPdfUrlProvider,
    private readonly shareConfiguratorUrlProvider: IShareConfiguratorUrlProvider,
    private readonly getContextCallback: () => ConfigurationShareContext,
  ) {
    this.downloadAsPdfCommand = new AsyncCommand(() => this.downloadAsPdf());
    this.downloadZipCommand = new AsyncCommand(() => this.downloadAsZip());
    this.downloadImageCommand = new AsyncCommand(() => this.downloadAsSingleImage());
    this.zipBuilder = new ZipBuilder(this.client, this.navigation);
    this.ean13CodeState = new Ean13CodeShareState(client);
  }

  public initialize(shoppingContext: ShoppingContext, storeContext: StoreContext): void {
    this.pdfUrlProvider.initialize(shoppingContext, storeContext);
    this.shareConfiguratorUrlProvider.initialize(shoppingContext, storeContext);
    this.shoppingContext = shoppingContext;
  }

  @action
  async downloadAsPdf() {
    // Ensure image is generated to make sure that it is rendered in pdf
    const imageUrl = this.imageUrlForPdf;
    await this.imagePreloader.load([imageUrl]);

    const context = this.getContextCallback();
    const pdfGenerationUrl = this.pdfUrlProvider.getPdfUrl(context.productNumber, context.query);
    const command = new GenerateConfigurationPdfCommand({
      configurationCode: this.code,
      pdfGenerationUrl,
      shoppingContext: this.shoppingContext,
    });
    const response = await this.client.send(command);

    this.navigation.redirect(response.fileUrl);
  }

  @action
  async downloadAsZip() {
    const { visualizationMode, visualizationShots } = this.getContextCallback();
    await this.zipBuilder.downloadZipConfigurationImages(
      this.code,
      {
        shots: visualizationShots,
        outputFilename: this.zipName,
      },
      visualizationMode,
    );
  }

  @action
  async downloadAsSingleImage() {
    if (!this.downloadImageUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = this.downloadImageUrl;
    link.download = this.downloadImageName;
    link.click();
  }

  public get imageUrlForPdf() {
    const { visualizationShots } = this.getContextCallback();
    return FinalVisualizationBuilder.buildUrl(this.code, visualizationShots[0], 'jpg', 1000, 1192);
  }

  @computed
  get absoluteShareUrl() {
    const context = this.getContextCallback();
    return this.shareConfiguratorUrlProvider.getUrl(this.code, context.query.quantity);
  }
}
